import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChatBubbleLeftRightIcon, ChevronDownIcon, PencilIcon, CheckIcon, EyeIcon } from '@heroicons/react/24/outline';
import { smsAPI, type SmsTemplate, authAPI } from '../services/api';
import { userAssistantConfigService, type UserAssistantConfig } from '../services/userAssistantConfigService';

type BulkLead = { id: string; name: string; phone: string };

type BulkSMSModalProps = {
  isOpen: boolean;
  onClose: () => void;
  leads: BulkLead[];
  setToast: (t: { type: 'success' | 'error'; message: string } | null) => void;
};

function fillPlaceholders(body: string, leadName: string, agentName: string, callbackNumber: string): string {
  if (!body) return body;
  let result = body;
  result = result.replace(/\{\{lead_name\}\}/g, leadName || '');
  result = result.replace(/\{\{agent_name\}\}/g, agentName || '');
  result = result.replace(/\{\{callback_number\}\}/g, callbackNumber || '');
  result = result.replace(/\{\{appointment_time\}\}/g, '');
  return result;
}

const BulkSMSModal: React.FC<BulkSMSModalProps> = ({ isOpen, onClose, leads, setToast }) => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [sending, setSending] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedBody, setEditedBody] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [progress, setProgress] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [agentName, setAgentName] = useState('');
  const [callbackNumber, setCallbackNumber] = useState('');
  const [previewLeadId, setPreviewLeadId] = useState<string | null>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId('');
    setEditedBody('');
    setIsEditing(false);
    setProgress(null);
    setPreviewLeadId(leads.length > 0 ? leads[0].id : null);
    setLoading(true);
    smsAPI
      .getTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));

    userAssistantConfigService.getConfig()
      .then((cfg: UserAssistantConfig) => setAgentName(cfg.agent_name || ''))
      .catch(() => {});
    authAPI.getCurrentUser()
      .then((u) => setCallbackNumber(u.phone || ''))
      .catch(() => {});
  }, [isOpen, leads]);

  const selectedTemplate = templates.find((t) => t.id === selectedId);

  const prevSelectedIdRef = useRef('');
  useEffect(() => {
    if (selectedId && selectedId !== prevSelectedIdRef.current) {
      const tpl = templates.find((t) => t.id === selectedId);
      if (tpl) setEditedBody(tpl.body);
      setIsEditing(false);
    }
    if (!selectedId) { setEditedBody(''); setIsEditing(false); }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId, templates]);

  const previewLead = previewLeadId ? leads.find((l) => l.id === previewLeadId) : null;
  const previewMessage = previewLead && editedBody
    ? fillPlaceholders(editedBody, previewLead.name, agentName, callbackNumber)
    : null;

  const handleBulkSend = async () => {
    if (!selectedId || leads.length === 0) return;
    setSending(true);
    setProgress(null);
    try {
      const result = await smsAPI.bulkSendSms({
        lead_ids: leads.map((l) => l.id),
        template_id: selectedId,
        custom_body: editedBody.trim() || undefined,
      });
      setProgress({ sent: result.sent, failed: result.failed, total: result.total });
      if (result.failed === 0) {
        setToast({ type: 'success', message: `SMS sent to all ${result.sent} leads (${result.total_credits_deducted} credits used)` });
      } else {
        setToast({ type: 'error', message: `${result.sent} sent, ${result.failed} failed out of ${result.total}` });
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || 'Bulk SMS failed';
      setToast({ type: 'error', message: detail });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />
      <div className="relative z-10 w-full max-w-5xl rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50 sticky top-0 bg-slate-800 z-10">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Bulk Send SMS</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-sm font-semibold">
              {leads.length}
            </span>
            <p className="text-sm text-gray-300">
              leads selected — click a lead to preview their personalized message
            </p>
          </div>

          <div className="max-h-28 overflow-y-auto custom-scrollbar rounded-lg border border-slate-600/50 bg-slate-700/20">
            <div className="flex flex-wrap gap-1 p-1.5">
              {leads.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setPreviewLeadId(previewLeadId === l.id ? null : l.id)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-colors cursor-pointer ${
                    previewLeadId === l.id
                      ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                      : 'bg-slate-700/50 border-slate-600/30 text-gray-300 hover:bg-slate-600/50 hover:border-slate-500/40'
                  }`}
                >
                  <span className="font-medium text-white">{l.name}</span>
                  <span className="text-gray-400">{l.phone}</span>
                  {previewLeadId === l.id && <EyeIcon className="w-3 h-3 text-emerald-400 ml-0.5" />}
                </button>
              ))}
            </div>
          </div>

          <div ref={dropdownRef} className="relative">
            <label id="bulk-sms-template-label" className="block text-sm font-medium text-gray-300 mb-1.5">
              Select template *
            </label>
            {loading ? (
              <p className="text-gray-400 text-sm">Loading templates...</p>
            ) : templates.length === 0 ? (
              <p className="text-gray-400 text-sm">
                No SMS templates yet.{' '}
                <a href="/sms-templates" className="text-cyan-400 hover:underline">Create one</a> first.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                  aria-labelledby="bulk-sms-template-label"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-left text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {selectedId ? templates.find((t) => t.id === selectedId)?.name ?? 'Choose a template' : 'Choose a template'}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {dropdownOpen && (
                  <ul role="listbox" aria-labelledby="bulk-sms-template-label" className="absolute left-0 right-0 top-full mt-1 z-20 py-1 rounded-lg bg-slate-800 border border-slate-600/50 shadow-xl max-h-48 overflow-y-auto custom-scrollbar">
                    <li role="option" aria-selected={!selectedId} onClick={() => { setSelectedId(''); setDropdownOpen(false); }} className={`px-3 py-2.5 cursor-pointer text-sm truncate ${!selectedId ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}>
                      Choose a template
                    </li>
                    {templates.map((t) => (
                      <li key={t.id} role="option" aria-selected={selectedId === t.id} onClick={() => { setSelectedId(t.id); setDropdownOpen(false); }} className={`px-3 py-2.5 cursor-pointer text-sm truncate ${selectedId === t.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}>
                        {t.name}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {selectedTemplate && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-2.5">
                <p className="text-xs font-medium text-gray-400 mb-0.5">Template</p>
                <p className="text-sm text-gray-400 whitespace-pre-wrap leading-snug">{selectedTemplate.body}</p>
              </div>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-900/10 p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-emerald-400">Message to send</p>
                  {isEditing ? (
                    <button type="button" onClick={() => setIsEditing(false)} className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors text-xs font-medium" title="Save changes">
                      <CheckIcon className="w-3.5 h-3.5" /><span>Done</span>
                    </button>
                  ) : (
                    <button type="button" onClick={() => { setIsEditing(true); setTimeout(() => textareaRef.current?.focus(), 50); }} className="flex items-center gap-1 px-2 py-0.5 rounded text-gray-400 hover:text-emerald-400 hover:bg-white/10 transition-colors text-xs" title="Edit message">
                      <PencilIcon className="w-3.5 h-3.5" /><span>Edit</span>
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <>
                    <textarea ref={textareaRef} value={editedBody} onChange={(e) => setEditedBody(e.target.value)} rows={5} className="w-full px-2 py-1.5 bg-white/5 border border-emerald-500/30 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y leading-snug" />
                    <p className="text-xs text-gray-400 mt-0.5">{editedBody.length} characters</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{editedBody}</p>
                )}
              </div>
            </div>
          )}

          {previewLead && previewMessage && selectedTemplate && (
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-900/10 p-3">
              <p className="text-xs font-medium text-cyan-400 mb-1.5">
                Preview for <span className="text-white font-semibold">{previewLead.name}</span>
                <span className="text-gray-400 ml-1">({previewLead.phone})</span>
              </p>
              <p className="text-sm text-gray-200 whitespace-pre-wrap">{previewMessage}</p>
            </div>
          )}

          {!previewLead && selectedTemplate && !isEditing && (
            <p className="text-xs text-gray-400 text-center">Click a lead above to preview their personalized message</p>
          )}

          {progress && (
            <div className={`rounded-lg p-3 text-sm ${progress.failed === 0 ? 'bg-emerald-900/20 border border-emerald-500/30 text-emerald-300' : 'bg-amber-900/20 border border-amber-500/30 text-amber-300'}`}>
              Sent: <strong>{progress.sent}</strong> · Failed: <strong>{progress.failed}</strong> · Total: <strong>{progress.total}</strong>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors">
              {progress ? 'Close' : 'Cancel'}
            </button>
            {!progress && (
              <button
                type="button"
                onClick={handleBulkSend}
                disabled={sending || loading || !selectedId || templates.length === 0 || leads.length === 0}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? `Sending to ${leads.length} leads...` : `Send SMS to ${leads.length} leads`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSMSModal;
